// Shared case-detail rendering for HR and Manager perspectives. The data
// shape (`CaseDetail`) is identical between roles; the access path is what
// differs (`/api/cases/:id` vs `/api/hr/cases/:id`).

import { useState } from 'react';
import { Link } from 'react-router';
import {
  LOSS_TYPE_LABELS,
  SUPPORT_FRAMEWORK_STAGES,
  isLossSubframeworkType,
  TOUCHPOINT_COMPLETION_OUTCOMES,
  TOUCHPOINT_CONTACT_METHODS,
  TOUCHPOINT_CONTACT_METHOD_LABELS,
  TOUCHPOINT_OUTCOME_LABELS,
  type CaseDetail,
  type CompleteTouchpointRequest,
  type PendingTouchpointSummary,
  type TouchpointCompletionOutcome,
  type TouchpointContactMethod,
  type TouchpointSummary,
  type TouchpointType,
} from '@besideu/shared/cases';
import {
  computeDisplayStatus,
  DISPLAY_STATUS_LABELS,
  type TouchpointDisplayStatus,
} from '@besideu/shared/touchpoint-status';
import { TOUCHPOINT_RATIONALE } from '@besideu/shared/touchpoint-rationale';
import { isBriefableTouchpoint } from './ManagerMaeveTouchpointPrep.tsx';
import { prefSentence } from '@/lib/format.ts';
import { Button } from './ui/Button.tsx';
import { Field, Select, TextInput } from './ui/Field.tsx';
import { StatusPill, ToneFor, humaniseStatus } from './ui/StatusPill.tsx';
import { ManagerSelect, type ManagerOption } from './ManagerSelect.tsx';

const TOUCHPOINT_LABELS: Record<TouchpointSummary['type'], string> = {
  Day_3: 'Day 3',
  Week_1: 'Week 1',
  Pre_Return: 'Pre-return',
  Week_1_Back: 'Week 1 back',
  Month_1: 'Month 1',
  Month_3: 'Month 3',
  // PO Spec §3 (2026-06-21) — 12-month series expansion.
  Month_6: 'Month 6',
  Month_9: 'Month 9',
  Month_12: 'Month 12',
  Anniversary: 'Anniversary',
};

export function CaseDetailView({
  data,
  onMarkFirstResponseSent,
  markSentPending,
  onCompleteTouchpoint,
  completingTouchpointId,
  onCompleteHrReview,
  hrReviewPendingId,
  onPrepareTouchpoint,
  managers,
  onReassignManager,
  reassignPending,
  reassignError,
  reassignNote,
}: {
  data: CaseDetail;
  // Manager-only — provide on the manager case detail to surface the slice 9
  // "I've sent it" button when `firstResponseSentAt` is null. HR omits.
  onMarkFirstResponseSent?: () => void;
  markSentPending?: boolean;
  // Manager-only — provide to surface the §16 structured completion form. HR
  // omits, so the timeline stays read-only on the HR side.
  onCompleteTouchpoint?: (touchpointId: string, completion: CompleteTouchpointRequest) => void;
  completingTouchpointId?: string | null;
  // HR-only (§15/§23) — provide to surface "Mark review complete" on touch
  // points flagged `HR_Review_Needed`. Manager omits.
  onCompleteHrReview?: ((touchpointId: string) => void) | undefined;
  hrReviewPendingId?: string | null | undefined;
  // Manager-only — provide to surface "Prepare for this touch point with
  // Maeve" inside each briefable row's rationale panel. The caller owns what
  // that means (select the touch point in the prep card, switch tabs, move
  // focus), because the caller owns the prep card. HR omits: no Maeve tab.
  onPrepareTouchpoint?: ((touchpointId: string) => void) | undefined;
  // HR-only — case-scoped manager reassignment (does NOT touch the
  // employee's default `user_manager_mappings` entry, see
  // routes/hr.ts POST /cases/:id/reassign-manager). Manager omits both, so
  // the Manager field stays plain text on the manager-facing case detail.
  managers?: ManagerOption[];
  onReassignManager?: (managerId: string) => void;
  reassignPending?: boolean;
  reassignError?: string | null;
  // Case-scoped reassignment deliberately doesn't touch the employee's
  // default `user_manager_mappings` entry — set when the last reassign left
  // that default pointing at a non-Active manager, so HR isn't silently
  // left with a stale default.
  reassignNote?: string | null;
}) {
  const c = data.case;
  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{c.employee.name}</h1>
        <p className="mt-2 flex flex-wrap items-center gap-2 text-muted-foreground">
          <span>{c.deceasedRelationship}</span>
          <span aria-hidden>·</span>
          <span>loss {c.dateOfLoss}</span>
          <StatusPill tone={ToneFor(c.status)}>{humaniseStatus(c.status)}</StatusPill>
        </p>
      </header>

      <dl className="grid gap-4 rounded-lg border border-border p-6 sm:grid-cols-2">
        <Detail label="Employee">{c.employee.name}</Detail>
        <Detail label="Manager">
          {onReassignManager ? (
            <div className="flex flex-col gap-1">
              <ManagerSelect
                ariaLabel={`Manager for ${c.employee.name}`}
                value={c.manager.id}
                managers={managers}
                currentManager={c.manager}
                disabled={reassignPending ?? false}
                onChange={onReassignManager}
              />
              {reassignError ? (
                <p className="text-xs text-traffic-red" role="alert">
                  {reassignError}
                </p>
              ) : null}
              {reassignNote ? (
                <p className="text-xs text-traffic-amber" role="status">
                  {reassignNote}
                </p>
              ) : null}
            </div>
          ) : (
            c.manager.name
          )}
        </Detail>
        <Detail label="Relationship">{c.deceasedRelationship}</Detail>
        <Detail label="Type of loss">
          {c.lossType ? LOSS_TYPE_LABELS[c.lossType] : 'Not recorded'}
          {isLossSubframeworkType(c.lossType) ? (
            <span className="ml-2 inline-flex items-center rounded-full bg-traffic-amber/15 px-2 py-0.5 text-xs font-medium text-traffic-amber-text">
              Sub-framework — HR review
            </span>
          ) : null}
        </Detail>
        <Detail label="Date of loss">{c.dateOfLoss}</Detail>
        <Detail label="Return-to-work date">{c.returnToWorkDate ?? '—'}</Detail>
        <Detail label="First response sent">
          {c.firstResponseSentAt ? (
            new Date(c.firstResponseSentAt).toLocaleString()
          ) : (
            <span className="flex flex-wrap items-center gap-2">
              <span>Not yet</span>
              {c.hasOverdueFirstResponse ? <NoResponseBadge /> : null}
              {onMarkFirstResponseSent ? (
                <Button
                  variant="secondary"
                  loading={markSentPending ?? false}
                  onClick={onMarkFirstResponseSent}
                  className="py-1.5 text-sm"
                >
                  I&rsquo;ve sent it
                </Button>
              ) : null}
            </span>
          )}
        </Detail>
        <Detail label="Created">{new Date(c.createdAt).toLocaleString()}</Detail>
        <Detail label="Support space activated">{c.supportSpaceActivated}</Detail>
        <Detail label="Resources accessed">
          {c.resourcesAccessed === 'Yes' ? (
            <span className="flex flex-wrap items-center gap-2">
              <span>Yes</span>
              <span className="text-xs text-muted-foreground">
                ({c.resourcesAccessedCount} event{c.resourcesAccessedCount === 1 ? '' : 's'})
              </span>
            </span>
          ) : (
            'No'
          )}
        </Detail>
      </dl>

      {/* §6/§8 — the 12-month pathway shown as a stage-grouped timeline.
          Each stage card lists its touch point(s) with a colour-independent
          status chip (§24). Calm, structured, non-clinical (§25). */}
      <section>
        {/* "Touch point timeline", NOT "Support pathway timeline" (PO
            2026-08-06): since the record itself is now called a Support
            Pathway, the old heading nested the term inside itself. This
            section is the touch points within the pathway, so it takes the
            narrower name — "touch point" (two words) is already the
            user-facing term elsewhere in this file and in the HR flags. */}
        <h2 className="text-base font-semibold">Touch point timeline</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          The 12-month framework, grouped by stage. Reminders are dispatched at random times between
          14:00 and 20:00 UK on the scheduled day.
        </p>
        <ol className="mt-4 space-y-4">
          {SUPPORT_FRAMEWORK_STAGES.map((stage) => {
            const stageTypes = stage.types as readonly TouchpointType[];
            const rows = data.touchpoints
              .filter((t) => stageTypes.includes(t.type))
              .sort((a, b) => a.scheduledFor.localeCompare(b.scheduledFor));
            const pendingRows = data.pendingTouchpoints.filter((p) => stageTypes.includes(p.type));
            // A stage with no materialised rows yet is a not-yet-scheduled RTW
            // stage (Stages 2-6, pending until the return-to-work date is
            // confirmed), not an absent one — render its pending preview
            // rather than dropping the stage from the timeline (PO
            // observation: the pathway was missing stages for exactly this
            // reason).
            if (rows.length === 0 && pendingRows.length === 0) return null;
            return (
              <li key={stage.stage} className="rounded-md border border-border">
                <div className="flex items-baseline justify-between gap-2 border-b border-border bg-muted/30 px-4 py-2">
                  <span className="text-sm font-semibold">
                    Stage {String(stage.stage).padStart(2, '0')} · {stage.name}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {stage.anchor === 'rtw' ? 'from return-to-work date' : 'from date of loss'}
                  </span>
                </div>
                <ul className="divide-y divide-border">
                  {rows.map((ci) => (
                    <TimelineRow
                      key={ci.id}
                      touchpoint={ci}
                      onCompleteTouchpoint={onCompleteTouchpoint}
                      completingTouchpointId={completingTouchpointId}
                      onCompleteHrReview={onCompleteHrReview}
                      hrReviewPendingId={hrReviewPendingId}
                      onPrepareTouchpoint={onPrepareTouchpoint}
                    />
                  ))}
                  {pendingRows.map((p) => (
                    <PendingTimelineRow
                      key={p.id}
                      touchpoint={p}
                      // Only the Manager surface has a "Support plan" tab to
                      // link to (`onCompleteTouchpoint` is Manager-only —
                      // HR's read-only case view omits it, same signal the
                      // rest of this component already uses).
                      showConfirmRtwLink={Boolean(onCompleteTouchpoint)}
                    />
                  ))}
                </ul>
              </li>
            );
          })}
        </ol>
      </section>
    </div>
  );
}

// Shared row scaffold between a live `TimelineRow` and a not-yet-materialised
// `PendingTimelineRow` — keeps the two in visual lockstep (spacing, wrapping)
// without a shared component, since their content differs enough (actions,
// completion state, preference) that merging them would cost more clarity
// than it buys.
const TIMELINE_ROW_CLASS =
  'flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-4 py-3 text-sm';

// A single touch point inside a stage card. Collapsed by default to the
// essentials (name · timing · status chip + completion read-back); the
// completion form + the employee's preference expand below (§8 progressive
// disclosure — the immediate action first, detail underneath).
function TimelineRow({
  touchpoint: ci,
  onCompleteTouchpoint,
  completingTouchpointId,
  onCompleteHrReview,
  hrReviewPendingId,
  onPrepareTouchpoint,
}: {
  touchpoint: TouchpointSummary;
  onCompleteTouchpoint?:
    | ((touchpointId: string, completion: CompleteTouchpointRequest) => void)
    | undefined;
  completingTouchpointId?: string | null | undefined;
  onCompleteHrReview?: ((touchpointId: string) => void) | undefined;
  hrReviewPendingId?: string | null | undefined;
  onPrepareTouchpoint?: ((touchpointId: string) => void) | undefined;
}) {
  const prefSummary = touchpointPreferenceSummary(ci.preference, ci.status);
  const display = computeDisplayStatus(
    ci.status,
    new Date(`${ci.scheduledFor}T00:00:00.000Z`),
    new Date(),
  );
  return (
    <li className={TIMELINE_ROW_CLASS}>
      <span className="flex flex-wrap items-center gap-3">
        <span className="font-medium">{TOUCHPOINT_LABELS[ci.type]}</span>
        <span className="text-muted-foreground">{ci.scheduledFor}</span>
        <StatusChip status={display} />
        {/* §7 "Adjusted" — derived from the rescheduled audit; orthogonal to the
            status chip (a rescheduled touch point is still Due soon / etc.). */}
        {ci.wasRescheduled ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
            <span aria-hidden>↻</span>Updated
          </span>
        ) : null}
      </span>
      <span className="flex flex-wrap items-center gap-2 text-muted-foreground">
        {ci.completedAt ? (
          <span>
            {completionSummary(ci)} {new Date(ci.completedAt).toLocaleDateString()}
          </span>
        ) : (
          <span>{ci.dispatchedAt ? 'Reminder sent' : null}</span>
        )}
      </span>
      <WhyThisTouchpoint
        touchpointId={ci.id}
        type={ci.type}
        onPrepareWithMaeve={
          onPrepareTouchpoint && isBriefableTouchpoint(ci)
            ? () => onPrepareTouchpoint(ci.id)
            : undefined
        }
      />
      {!ci.completedAt && onCompleteTouchpoint ? (
        <TouchpointCompletionForm
          pending={completingTouchpointId === ci.id}
          onSubmit={(completion) => onCompleteTouchpoint(ci.id, completion)}
        />
      ) : null}
      {/* §15/§23 — HR resolves a touch point flagged for review. */}
      {ci.status === 'HR_Review_Needed' && onCompleteHrReview ? (
        <Button
          variant="secondary"
          className="py-1 text-xs"
          loading={hrReviewPendingId === ci.id}
          onClick={() => onCompleteHrReview(ci.id)}
        >
          Mark review complete
        </Button>
      ) : null}
      {prefSummary ? (
        <p className="w-full text-xs text-muted-foreground">
          <span className="font-medium text-foreground">Their preference: </span>
          {prefSummary}
        </p>
      ) : null}
    </li>
  );
}

// A not-yet-scheduled RTW-anchored touch point — no completion actions
// (nothing has been scheduled yet). Uses the same `StatusChip` vocabulary as
// `TimelineRow` (`Not_Started` — §7's own display status for "an RTW-anchored
// spec before the return date is confirmed", see `touchpoint-status.ts`)
// rather than a bespoke pill, so anything that reads the chip's label/tone
// stays in one place. The manager surface links to the "Support plan" tab
// where the return-to-work date is confirmed; HR's read-only view (no plan
// tab) gets plain text instead.
function PendingTimelineRow({
  touchpoint: p,
  showConfirmRtwLink,
}: {
  touchpoint: PendingTouchpointSummary;
  showConfirmRtwLink: boolean;
}) {
  return (
    <li className={`${TIMELINE_ROW_CLASS} text-muted-foreground`}>
      <span className="flex flex-wrap items-center gap-3">
        <span className="font-medium">{TOUCHPOINT_LABELS[p.type]}</span>
        <span>{p.scheduledForEstimate ?? 'Not yet scheduled'}</span>
        <StatusChip status="Not_Started" />
      </span>
      <span className="flex flex-wrap items-center gap-2">
        <span>Pending return-to-work date</span>
        {showConfirmRtwLink ? (
          <Link to="?tab=plan" className="text-xs underline-offset-4 hover:underline">
            Confirm return-to-work date →
          </Link>
        ) : null}
      </span>
      {/* Deliberately offered here too: "why does this one have no date yet?"
          is the question a pending row provokes, and the rationale's `timing`
          line is the answer (it names the anchor the date is waiting on).
          No Maeve link — there is no touch point to brief against until the
          spec materialises (`ManagerMaeveTouchpointPrep` filters to real
          `Pending`/`Overdue` rows). */}
      <WhyThisTouchpoint touchpointId={p.id} type={p.type} />
    </li>
  );
}

// §8 progressive disclosure — the collapsed row is the action; the framework's
// reasoning sits one click underneath it. Static copy, not a Maeve turn: see
// the header of `@besideu/shared/touchpoint-rationale`, which also explains why
// nothing here is attributed to Maeve.
function WhyThisTouchpoint({
  touchpointId,
  type,
  onPrepareWithMaeve,
}: {
  // Keys the panel's DOM id off the row, not the type: a stage can hold both a
  // materialised row and a pending spec, and two `id="why-Month_1"` nodes on
  // one page would break the `aria-controls` association for both.
  touchpointId: string;
  type: TouchpointType;
  /**
   * Manager-only, and only for a touch point Maeve can actually brief against
   * (`isBriefableTouchpoint`). Absent on HR's read-only view, which has no
   * "Ask Maeve" tab, and on any row the prep card would refuse to select —
   * offering it there would be a control that quietly does nothing.
   */
  onPrepareWithMaeve?: (() => void) | undefined;
}) {
  const [open, setOpen] = useState(false);
  const rationale = TOUCHPOINT_RATIONALE[type];
  const panelId = `why-${touchpointId}`;
  return (
    <div className="w-full">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={panelId}
        className="text-xs text-muted-foreground underline-offset-4 hover:underline"
      >
        <span aria-hidden>{open ? '▾' : '▸'}</span> Why this touch point?
        {/* Up to ten of these sit on one page, so the visible label alone
            would give every one of them the identical accessible name — an
            AT user listing the page's buttons hears "Why this touch point?"
            ten times with nothing to tell them apart. */}
        <span className="sr-only"> — {TOUCHPOINT_LABELS[type]}</span>
      </button>
      {/* Always rendered, hidden via `hidden` rather than unmounted: the
          button's `aria-controls` must point at a node that EXISTS, and in the
          default (collapsed) state a conditionally-rendered panel leaves it
          dangling. Same reasoning as `TabPanel` in `ui/Tabs.tsx`. */}
      <div
        id={panelId}
        hidden={!open}
        className="mt-2 space-y-1 rounded-md border border-border bg-muted/20 p-3 text-xs"
      >
        <p className="font-medium text-foreground">{rationale.timing}</p>
        <p className="text-muted-foreground">{rationale.why}</p>
        <p className="text-muted-foreground">
          <span className="font-medium text-foreground">What it&rsquo;s for: </span>
          {rationale.focus}
        </p>
        {onPrepareWithMaeve ? (
          <p className="pt-1">
            {/* A button, not a `<Link to="?tab=maeve">`: it selects THIS touch
                point in the prep card and starts its briefing, which a plain
                tab link never did (it just changed tabs and left the picker
                empty). Parent state rather than a URL param — see
                `autoStartTouchpointId`. */}
            <button
              type="button"
              onClick={onPrepareWithMaeve}
              className="underline-offset-4 hover:underline"
            >
              Prepare for this touch point with Maeve →
              <span className="sr-only"> ({TOUCHPOINT_LABELS[type]})</span>
            </button>
          </p>
        ) : null}
      </div>
    </div>
  );
}

// §24 — status is conveyed by the LABEL (always present), with a leading glyph
// and a tone colour as SECONDARY cues. Never colour alone.
const STATUS_CHIP: Record<TouchpointDisplayStatus, { glyph: string; tone: string }> = {
  Not_Started: { glyph: '•', tone: 'bg-muted text-muted-foreground' },
  Scheduled: { glyph: '•', tone: 'bg-muted text-muted-foreground' },
  Due_Soon: { glyph: '◷', tone: 'bg-traffic-amber/15 text-traffic-amber-text' },
  Due_Today: { glyph: '◉', tone: 'bg-traffic-amber/15 text-traffic-amber-text' },
  Overdue: { glyph: '!', tone: 'bg-traffic-red/10 text-traffic-red' },
  Completed: { glyph: '✓', tone: 'bg-traffic-green/15 text-traffic-green-text' },
  Paused: { glyph: '⏸', tone: 'bg-muted text-muted-foreground' },
  Skipped: { glyph: '–', tone: 'bg-muted text-muted-foreground' },
  HR_Review_Needed: { glyph: '!', tone: 'bg-traffic-amber/15 text-traffic-amber-text' },
};

function StatusChip({ status }: { status: TouchpointDisplayStatus }) {
  const { glyph, tone } = STATUS_CHIP[status];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}
    >
      <span aria-hidden>{glyph}</span>
      {DISPLAY_STATUS_LABELS[status]}
    </span>
  );
}

// Read-back of a completed touch point's §16 outcome for the timeline, e.g.
// "Contact made · Email". Falls back to a plain "Completed" for older rows that
// predate structured completion.
function completionSummary(ci: TouchpointSummary): string {
  if (ci.status === 'Skipped') return 'Skipped';
  const parts: string[] = [];
  parts.push(ci.outcome ? TOUCHPOINT_OUTCOME_LABELS[ci.outcome] : 'Completed');
  if (ci.contactMethod) parts.push(TOUCHPOINT_CONTACT_METHOD_LABELS[ci.contactMethod]);
  if (ci.status === 'HR_Review_Needed') parts.push('HR review');
  return parts.join(' · ');
}

// §16 structured completion form. Collapsed to a single "Complete touch point"
// button; expands to capture outcome (required) + optional contact method,
// follow-up / HR-review flags, and factual notes. The spec is explicit: no long
// notes are forced, and the manager is never asked to assess emotional state.
export function TouchpointCompletionForm({
  pending,
  onSubmit,
  startOpen = false,
}: {
  pending: boolean;
  onSubmit: (completion: CompleteTouchpointRequest) => void;
  // Journey 04 focused check-in page renders it already expanded (the whole
  // page IS the completion moment); the case-detail timeline keeps it
  // collapsed behind "Complete touch point".
  startOpen?: boolean;
}) {
  const [open, setOpen] = useState(startOpen);
  const [outcome, setOutcome] = useState<TouchpointCompletionOutcome>('contact_made');
  const [contactMethod, setContactMethod] = useState<TouchpointContactMethod | ''>('');
  const [followUpNeeded, setFollowUpNeeded] = useState(false);
  const [hrReviewNeeded, setHrReviewNeeded] = useState(false);
  const [notes, setNotes] = useState('');

  if (!open) {
    return (
      <div className="w-full">
        <Button variant="secondary" className="py-1 text-xs" onClick={() => setOpen(true)}>
          Complete touch point
        </Button>
      </div>
    );
  }

  return (
    <form
      className="grid w-full gap-3 rounded-md border border-border bg-muted/20 p-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          outcome,
          contactMethod: contactMethod || null,
          followUpNeeded,
          hrReviewNeeded,
          notes: notes.trim() ? notes.trim() : null,
        });
      }}
    >
      <Field label="What happened?">
        {(id, descProps) => (
          <Select
            id={id}
            {...descProps}
            value={outcome}
            onChange={(e) => setOutcome(e.target.value as TouchpointCompletionOutcome)}
          >
            {TOUCHPOINT_COMPLETION_OUTCOMES.map((o) => (
              <option key={o} value={o}>
                {TOUCHPOINT_OUTCOME_LABELS[o]}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <Field label="Contact method (optional)">
        {(id, descProps) => (
          <Select
            id={id}
            {...descProps}
            value={contactMethod}
            onChange={(e) => setContactMethod(e.target.value as TouchpointContactMethod | '')}
          >
            <option value="">Not recorded</option>
            {TOUCHPOINT_CONTACT_METHODS.map((m) => (
              <option key={m} value={m}>
                {TOUCHPOINT_CONTACT_METHOD_LABELS[m]}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={followUpNeeded}
          onChange={(e) => setFollowUpNeeded(e.target.checked)}
        />
        Follow-up needed
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={hrReviewNeeded}
          onChange={(e) => setHrReviewNeeded(e.target.checked)}
        />
        HR review needed
      </label>

      <Field label="Notes (optional, factual)">
        {(id, descProps) => (
          <TextInput
            id={id}
            {...descProps}
            value={notes}
            maxLength={2000}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. agreed a phased return check-in next week"
          />
        )}
      </Field>

      <div className="flex items-center gap-2">
        <Button type="submit" className="py-1 text-xs" loading={pending}>
          Save completion
        </Button>
        <button
          type="button"
          className="text-xs underline-offset-4 hover:underline disabled:opacity-50"
          disabled={pending}
          onClick={() => setOpen(false)}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// Summarise the §9.4.3 per-touch-point preference for the manager/HR view.
// A `not_now` preference only pauses (Suppresses, and so hides) a touch point
// that was still `Pending` — see `setTouchpointPreference`. A touch point HR
// still needs to review (`HR_Review_Needed`) stays visible even after the
// employee asks to pause it, because HR resolving the underlying concern
// overrides the employee's preference; that case must say so explicitly here
// rather than silently showing nothing, which would look like the request
// never happened.
function touchpointPreferenceSummary(
  pref: TouchpointSummary['preference'],
  status: TouchpointSummary['status'],
): string | null {
  if (!pref) return null;
  const parts: string[] = [];
  if (pref.contactPerson === 'manager') parts.push('prefers you to make contact');
  else if (pref.contactPerson === 'hr') parts.push('prefers HR to make contact');
  if (pref.preferEmail) parts.push('prefers email');
  if (pref.request === 'move_later') parts.push('would like to move this touch point');
  else if (pref.request === 'sooner') parts.push('would like to talk sooner');
  else if (pref.request === 'not_now' && status !== 'Suppressed')
    parts.push('asked to pause this, but it still needs review before it can be paused');
  if (pref.doNotShareWithTeam) parts.push('asked not to share this with the wider team');
  return prefSentence(parts);
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs tracking-wide text-muted-foreground uppercase">{label}</dt>
      <dd className="mt-1 text-sm">{children}</dd>
    </div>
  );
}

function NoResponseBadge() {
  return (
    <span
      className="inline-flex items-center rounded-full bg-traffic-red/10 px-2 py-0.5 text-xs font-medium text-traffic-red"
      title="Manager has not marked the first response sent within 24 hours of the support pathway being opened"
    >
      Overdue
    </span>
  );
}
